import { IShift, IShiftSession } from "../../types/shifts/Shift";
import { IShiftEnrolment } from "../../types/shifts/ShiftEnrolment";
import GameApi from "../api/gameApi";
import ShiftEnrolmentsApi from "../api/shiftEnrolmentsApi";
import ShiftSessionsApi from "../api/shiftSessionsApi";
import ShiftApi, { IProcessedShiftSessionTransport } from "../api/shiftsApi";
import * as types from "./actionTypes";

export function getSupervisableShifts() {
  return (dispatch: any) => {
    dispatch(getSupervisableShiftsStart());
    return ShiftApi.getSupervisableShifts()
      .then((data) => {
        dispatch(getSupervisableShiftsFulfilled(data?.shifts || []));
      })
      .catch((er) => {
        dispatch(getSupervisableShiftsRejected(er.message || er));
      });
  };
}

// getManageableShifts
export function getManageableShifts() {
  return (dispatch: any) => {
    dispatch(getManageableShiftsStart());
    return ShiftApi.getManageableShifts()
      .then((data) => {
        dispatch(getManageableShiftsFulfilled(data));
        return data;
      })
      .catch((er) => {
        dispatch(getManageableShiftsRejected(er.message));
        throw er;
      });
  };
}

export function getShiftById(shiftId: string, sessionId: string, gridView: string) {
  return (dispatch: any) => {
    const shiftKey = sessionId;
    dispatch(getShiftByIdStart(shiftKey));
    return ShiftApi.getShiftSession(shiftId, sessionId, gridView)
      .then((shift) => {
        dispatch(getShiftByIdFulfilled(shift)); // shift.shift.key
        return shift;
      })
      .catch((er) => {
        dispatch(getShiftByIdRejected(shiftKey, er.message || er));
        throw er;
      });
  };
}

export function rosterGame(
  gameId: string,
  person: string,
  position: string,
  hasApprentice: boolean,
  shiftSessionId: string,
  startDate: string,
  venue: string,
) {
  return (dispatch: any) => {
    if (!person) throw new Error("No person");
    dispatch(rosterGameStart(gameId));
    return GameApi.rosterGame(
      gameId,
      person,
      position,
      hasApprentice,
      shiftSessionId,
      [],
      startDate,
      venue,
    )
      .then((payload) => {
        dispatch(rosterGameFulfilled(payload));
        return payload;
      })
      .catch((er) => {
        dispatch(rosterGameRejected(gameId, { person, position }, er.message));
        throw er;
      });
  };
}

export function removeReferee(
  gameId: string,
  position: string,
  shiftSessionId: string,
  comment?: string,
) {
  return (dispatch: any) => {
    dispatch(rosterGameStart(gameId));
    return GameApi.removeReferee(gameId, position, shiftSessionId, comment)
      .then((payload) => dispatch(rosterGameFulfilled(payload)))
      .catch((er) => {
        dispatch(rosterGameRejected(gameId, { position }, er.message));
      });
  };
}

export function createShift(shift: Partial<IShift>) {
  return (dispatch: any) => {
    dispatch(createShiftStart(shift));
    return ShiftApi.createShift(shift)
      .then((shift) => {
        dispatch(createShiftFulfilled(shift[0]));
        return shift[0];
      })
      .catch((er) => {
        dispatch(createShiftRejected(er.message || er));
        throw er;
      });
  };
}

export function updateShift(shift: Partial<IShift>) {
  if (!shift._id) {
    throw new Error(
      "An internal error has occurred: no shift._id on the update",
    );
  }
  return (dispatch: any) => {
    dispatch(updateShiftStart(shift));
    return ShiftApi.updateShift(shift)
      .then((shift) => {
        dispatch(updateShiftFulfilled(shift));
        return shift[0];
      })
      .catch((er) => {
        dispatch(updateShiftRejected(er.message));
        throw er;
      });
  };
}

export function rosterShiftVenueSupervisor(
  sessionId: string,
  venueId: string,
  person: string,
  start: Date,
  finish: Date,
) {
  return (dispatch: any) => {
    dispatch(allocateSupervisorStart(sessionId, person));
    return ShiftApi.rosterShiftVenueSupervisor(
      sessionId,
      venueId,
      person,
      start,
      finish,
    )
      .then((resp) => {
        const r = {
          session: resp.data,
          availabilities: resp.availabilities,
          appointments: resp.appointments,
        };
        dispatch(allocateSupervisorFulfilled(r));
        return r;
      })
      .catch((er) => {
        dispatch(allocateSupervisorRejected(sessionId, er.message));
        throw er;
      });
  };
}

export function removeShiftVenueSupervisor(sessionId: string, venueId: string, appointmentId: string) {
  return (dispatch: any) => {
    dispatch(removeSupervisorStart(sessionId));
    return ShiftApi.removeShiftVenueSupervisor(
      sessionId,
      venueId,
      appointmentId,
    )
      .then((resp) => {
        const r = {
          session: resp.data,
          availabilities: resp.availabilities,
          appointments: resp.appointments,
        };
        dispatch(removeSupervisorFulfilled(r));
        return r;
      })
      .catch((er) => {
        dispatch(removeSupervisorRejected(sessionId, er.message));
        throw er;
      });
  };
}

// removeShiftVenueAdministrator
// rosterShiftVenueAdministrator


export function rosterShiftVenueAdministrator(
  sessionId: string,
  venueId: string,
  person: string,
  start: Date,
  finish: Date,
  usePersonalPayrate: boolean
) {
  return (dispatch: any) => {
    dispatch(allocateShiftAdminStart(sessionId, person));
    return ShiftApi.rosterShiftVenueAdministrator(
      sessionId,
      venueId,
      person,
      start,
      finish,
      usePersonalPayrate,
    )
      .then((resp) => {
        const r = {
          session: resp.data,
          appointments: resp.appointments,
        };
        dispatch(allocateShiftAdminFulfilled(r));
        return r;
      })
      .catch((er) => {
        dispatch(allocateShiftAdminRejected(sessionId, er.message));
        throw er;
      });
  };
}

export function removeShiftVenueAdministrator(sessionId: string, venueId: string, appointmentId: string) {
  return (dispatch: any) => {
    dispatch(removeShiftAdminStart(sessionId));
    return ShiftApi.removeShiftVenueAdmin(
      sessionId,
      venueId,
      appointmentId,
    )
      .then((resp) => {
        const r = {
          session: resp.data,
          appointments: resp.appointments,
        };
        dispatch(removeShiftAdminFulfilled(r));
        return r;
      })
      .catch((er) => {
        dispatch(removeShiftAdminRejected(sessionId, er.message));
        throw er;
      });
  };
}

export function publishShift(sessionId: string, sendNotifications: boolean = false) {
  return (dispatch: any) => {
    dispatch(publishSessionStarted(sessionId));
    console.log("Publishing session");
    return ShiftApi.publishShiftSession(sessionId, sendNotifications)
      .then((session) => {
        dispatch(publishSessionFulfilled(session));
        return session;
      })
      .catch((er) => {
        dispatch(publishSessionRejected(sessionId, er.message));
        throw er;
      });
  };
}

export function updateShiftEnrolment(enrolment: IShiftEnrolment) {
  return (dispatch: any) =>
    ShiftEnrolmentsApi.updateShiftEnrolment(enrolment).then((resp) => {
      dispatch(updateShiftEnrolmentFulfilled(resp));
      return resp;
    });
}

export function removeShiftEnrolment(shiftId: string, person: string, shiftSessionId?: string) {
  return (dispatch: any) =>
    ShiftEnrolmentsApi.archiveShiftEnrolment(shiftId, person, shiftSessionId).then((resp) => {
      dispatch(removeShiftEnrolmentFulfilled(shiftId, person, shiftSessionId));
      return resp;
    });
}

export function addShiftEnrolment(enrolment: IShiftEnrolment) {
  return (dispatch: any) =>
    ShiftEnrolmentsApi.updateShiftEnrolment(enrolment).then((resp) => {
      dispatch(addShiftEnrolmentFulfilled(resp));
      return resp;
    });
}

export function addShiftSessionEnrolment(enrolment: IShiftEnrolment) {
  return () => ShiftEnrolmentsApi.updateShiftEnrolment(enrolment);
}

export function updateShiftSession(shiftSession: Partial<IShiftSession>) {
  return (dispatch: any) =>
    ShiftSessionsApi.updateShiftSession(shiftSession).then((resp) => {
      dispatch(updateShiftSessionFulfilled(resp));
      return resp;
    });
}

export function removeShiftSession(shiftId: string, sessionId: string) {
  return (dispatch: any) =>
    ShiftSessionsApi.archiveShiftSession(sessionId).then((resp) => {
      dispatch(removeShiftSessionFulfilled(shiftId, sessionId));
      return resp;
    });
}

export function addShiftSession(shiftSession: IShiftSession) {
  return (dispatch: any) =>
    ShiftSessionsApi.createShiftSession(shiftSession).then((resp) => {
      dispatch(addShiftSessionFulfilled(resp));
      return resp;
    });
}

function rosterGameStart(gameId: string) {
  return {
    type: types.SHIFT_ROSTER_GAME_STARTED,
    payload: gameId,
  };
}

function rosterGameFulfilled(payload: any) {
  return {
    type: types.SHIFT_ROSTER_GAME_FULFILLED,
    payload,
  };
}

function rosterGameRejected(gameId: string, referee: any, message: string) {
  return {
    type: types.SHIFT_ROSTER_GAME_REJECTED,
    payload: {
      gameId,
      referee,
      message,
    },
  };
}

function allocateSupervisorStart(sessionId: string, person: string) {
  return {
    type: types.SHIFTS_ROSTER_SUPERVISOR_STARTED,
    payload: { sessionId, person },
  };
}

function allocateSupervisorFulfilled(payload: any) {
  return {
    type: types.SHIFTS_ROSTER_SUPERVISOR_FULFILLED,
    payload,
  };
}

function allocateSupervisorRejected(sessionId: string, error: any) {
  return {
    type: types.SHIFTS_ROSTER_SUPERVISOR_REJECTED,
    payload: { sessionId, error },
  };
}

function removeSupervisorStart(sessionId: string) {
  return {
    type: types.SHIFTS_REMOVE_SUPERVISOR_STARTED,
    payload: { sessionId },
  };
}

function removeSupervisorFulfilled(payload: any) {
  return {
    type: types.SHIFTS_REMOVE_SUPERVISOR_FULFILLED,
    payload,
  };
}

function removeSupervisorRejected(sessionId: string, error: any) {
  return {
    type: types.SHIFTS_REMOVE_SUPERVISOR_REJECTED,
    payload: { sessionId, error },
  };
}

function allocateShiftAdminStart(sessionId: string, person: string) {
  return {
    type: types.SHIFTS_ROSTER_ADMIN_STARTED,
    payload: { sessionId, person },
  };
}

function allocateShiftAdminFulfilled(payload: any) {
  return {
    type: types.SHIFTS_ROSTER_ADMIN_FULFILLED,
    payload,
  };
}

function allocateShiftAdminRejected(sessionId: string, error: any) {
  return {
    type: types.SHIFTS_ROSTER_ADMIN_REJECTED,
    payload: { sessionId, error },
  };
}

function removeShiftAdminStart(sessionId: string) {
  return {
    type: types.SHIFTS_REMOVE_ADMIN_STARTED,
    payload: { sessionId },
  };
}

function removeShiftAdminFulfilled(payload: any) {
  return {
    type: types.SHIFTS_REMOVE_ADMIN_FULFILLED,
    payload,
  };
}

function removeShiftAdminRejected(sessionId: string, error: any) {
  return {
    type: types.SHIFTS_REMOVE_ADMIN_REJECTED,
    payload: { sessionId, error },
  };
}

function getSupervisableShiftsStart() {
  return {
    type: types.SHIFTS_GET_SUPERVISABLE_STARTED,
  };
}

function getSupervisableShiftsFulfilled(shifts: IShift[]) {
  return {
    type: types.SHIFTS_GET_SUPERVISABLE_FULFILLED,
    payload: shifts,
  };
}

function getSupervisableShiftsRejected(error: any) {
  return {
    type: types.SHIFTS_GET_SUPERVISABLE_REJECTED,
    payload: error,
  };
}

function getShiftByIdStart(shiftKey: string) {
  return {
    type: types.SHIFTS_GET_SHIFT_STARTED,
    payload: shiftKey,
  };
}

function getShiftByIdFulfilled(shift: IProcessedShiftSessionTransport) {
  return {
    type: types.SHIFTS_GET_SHIFT_FULFILLED,
    payload: shift,
  };
}

function getShiftByIdRejected(shiftKey: string, error: any) {
  return {
    type: types.SHIFTS_GET_SHIFT_REJECTED,
    payload: { shiftKey, message: error },
  };
}

function getManageableShiftsStart() {
  return {
    type: types.SHIFTS_GET_MANAGEABLE_STARTED,
  };
}

function getManageableShiftsFulfilled(payload: any) {
  return {
    type: types.SHIFTS_GET_MANAGEABLE_FULFILLED,
    payload,
  };
}

function getManageableShiftsRejected(error: any) {
  return {
    type: types.SHIFTS_GET_MANAGEABLE_REJECTED,
    payload: error,
  };
}

function createShiftStart(shift: any) {
  return {
    type: types.SHIFTS_CREATE_SHIFT_STARTED,
    payload: shift,
  };
}

function createShiftFulfilled(shift: any) {
  return {
    type: types.SHIFTS_CREATE_SHIFT_FULFILLED,
    payload: shift,
  };
}

function createShiftRejected(er: any) {
  return {
    type: types.SHIFTS_CREATE_SHIFT_REJECTED,
    payload: er,
  };
}

function updateShiftStart(shift: any) {
  return {
    type: types.SHIFTS_UPDATE_SHIFT_STARTED,
    payload: shift,
  };
}

function updateShiftFulfilled(shift: any) {
  return {
    type: types.SHIFTS_UPDATE_SHIFT_FULFILLED,
    payload: shift,
  };
}

function updateShiftRejected(message: string) {
  return {
    type: types.SHIFTS_UPDATE_SHIFT_REJECTED,
    payload: message,
  };
}

function publishSessionStarted(sessionId: string) {
  return {
    type: types.SHIFTS_PUBLISH_SESSION_STARTED,
    payload: sessionId,
  };
}

function publishSessionFulfilled(session: any) {
  return {
    type: types.SHIFTS_PUBLISH_SESSION_FULFILLED,
    payload: session,
  };
}

function publishSessionRejected(sessionId: string, error: any) {
  return {
    type: types.SHIFTS_PUBLISH_SESSION_REJECTED,
    payload: { sessionId, error },
  };
}

function updateShiftEnrolmentFulfilled(enrolment: IShiftEnrolment) {
  return {
    type: types.SHIFTS_UPDATE_ENROLMENT_FULFILLED,
    payload: { enrolment },
  };
}

function removeShiftEnrolmentFulfilled(shiftId: string, person: string, shiftSessionId?: string) {
  return {
    type: types.SHIFTS_REMOVE_ENROLMENT_FULFILLED,
    payload: { shiftId, person, shiftSessionId },
  };
}

function addShiftEnrolmentFulfilled(enrolment: IShiftEnrolment) {
  return { type: types.SHIFTS_ADD_ENROLMENT_FULFILLED, payload: { enrolment } };
}

// tbh these should probably be moved to a shiftSessionReducer
function updateShiftSessionFulfilled(shiftSession: IShiftSession) {
  return {
    type: types.SHIFTS_UPDATE_SESSION_FULFILLED,
    payload: { shiftSession },
  };
}

function removeShiftSessionFulfilled(shiftId: string, sessionId: string) {
  return {
    type: types.SHIFTS_REMOVE_SESSION_FULFILLED,
    payload: { shiftId, sessionId },
  };
}

function addShiftSessionFulfilled(shiftSession: IShiftSession) {
  return {
    type: types.SHIFTS_ADD_SESSION_FULFILLED,
    payload: { shiftSession },
  };
}
